import React, { useState, useEffect } from 'react';
import { X, Bell, Calendar, AlertCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { ReminderType, ReminderPriority, ReminderCategory, Reminder } from '../../types/reminder';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useAuth } from '../../contexts/AuthContext';
import { toDateInputValue } from '../../utils/dateUtils';

interface EditReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  reminder: Reminder;
  petName?: string;
}

const EditReminderModal: React.FC<EditReminderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  reminder,
  petName
}) => {
  const { user } = useAuth();
  const [reminderTypes, setReminderTypes] = useState<ReminderType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    reminder_type_id: '',
    due_date: '',
    send_time: '09:00',
    priority: ReminderPriority.NORMAL,
    delivery_method: 'EMAIL',
    title: '',
    message: '',
    notes: ''
  });

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (isOpen) {
      fetchReminderTypes();
      // Pre-populate form with existing reminder data
      // Use toDateInputValue to extract date without UTC conversion issues
      const dueDateStr = toDateInputValue(reminder.due_date);
      
      // Extract time from scheduled_send_time if available (convert UTC to local time)
      let sendTime = '09:00'; // Default
      if (reminder.scheduled_send_time) {
        const scheduledDate = new Date(reminder.scheduled_send_time);
        // getHours() automatically converts to local timezone
        const hours = scheduledDate.getHours().toString().padStart(2, '0');
        const minutes = scheduledDate.getMinutes().toString().padStart(2, '0');
        sendTime = `${hours}:${minutes}`;
      }
      
      setFormData({
        reminder_type_id: reminder.reminder_type_id || '',
        due_date: dueDateStr,
        send_time: sendTime,
        priority: reminder.priority || ReminderPriority.NORMAL,
        delivery_method: reminder.delivery_method || 'EMAIL',
        title: reminder.reminder_type?.name || '',
        message: '',
        notes: reminder.notes || ''
      });
    }
  }, [isOpen, reminder]);

  const fetchReminderTypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(API_ENDPOINTS.REMINDER_TYPES.LIST, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setReminderTypes(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to fetch reminder types:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      
      if (!user?.practice_id) {
        throw new Error('Practice ID not found. Please log in again.');
      }
      
      // Combine date and time in user's local timezone, then convert to UTC
      const localDateTime = new Date(`${formData.due_date}T${formData.send_time}`);
      const scheduledSendTime = localDateTime.toISOString();
      
      const response = await fetch(API_ENDPOINTS.REMINDERS.UPDATE(reminder.id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          due_date: new Date(formData.due_date).toISOString(),
          scheduled_send_time: scheduledSendTime,
          priority: formData.priority,
          notes: formData.notes || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update reminder');
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleReminderTypeChange = (typeId: string) => {
    const selectedType = reminderTypes.find(t => t.id === typeId);
    if (selectedType) {
      setFormData(prev => ({
        ...prev,
        reminder_type_id: typeId,
        title: selectedType.name,
        message: selectedType.description || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        reminder_type_id: typeId
      }));
    }
  };

  const getCategoryColor = (category: ReminderCategory) => {
    const colors = {
      [ReminderCategory.VACCINATION]: 'bg-purple-100 text-purple-700',
      [ReminderCategory.CHECKUP]: 'bg-blue-100 text-blue-700',
      [ReminderCategory.MEDICATION]: 'bg-green-100 text-green-700',
      [ReminderCategory.APPOINTMENT]: 'bg-orange-100 text-orange-700',
      [ReminderCategory.FOLLOW_UP]: 'bg-yellow-100 text-yellow-700',
      [ReminderCategory.GROOMING]: 'bg-pink-100 text-pink-700',
      [ReminderCategory.DENTAL]: 'bg-teal-100 text-teal-700',
      [ReminderCategory.PARASITE_PREVENTION]: 'bg-red-100 text-red-700',
      [ReminderCategory.LAB_WORK]: 'bg-indigo-100 text-indigo-700',
      [ReminderCategory.PRESCRIPTION_REFILL]: 'bg-cyan-100 text-cyan-700',
      [ReminderCategory.OTHER]: 'bg-gray-100 text-gray-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-30 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Edit Reminder</h2>
                {petName && <p className="text-sm text-gray-500">for {petName}</p>}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Reminder Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder Type *
                </label>
                <select
                  value={formData.reminder_type_id}
                  onChange={(e) => handleReminderTypeChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a reminder type...</option>
                  {reminderTypes
                    .filter(type => type.is_enabled)
                    .map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name} ({type.category})
                      </option>
                    ))}
                </select>
                {reminderTypes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {reminderTypes
                      .filter(type => type.is_enabled)
                      .slice(0, 6)
                      .map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => handleReminderTypeChange(type.id)}
                          className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                            formData.reminder_type_id === type.id
                              ? 'bg-blue-600 text-white'
                              : getCategoryColor(type.category)
                          }`}
                        >
                          {type.name}
                        </button>
                      ))}
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                  placeholder="e.g., Annual Rabies Vaccination"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Due Date, Send Time, and Priority */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Due Date *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                      required
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Send Time *
                  </label>
                  <input
                    type="time"
                    value={formData.send_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, send_time: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority *
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as ReminderPriority }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={ReminderPriority.LOW}>Low</option>
                    <option value={ReminderPriority.NORMAL}>Normal</option>
                    <option value={ReminderPriority.HIGH}>High</option>
                    <option value={ReminderPriority.URGENT}>Urgent</option>
                  </select>
                </div>
              </div>

              {/* Delivery Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Method *
                </label>
                <select
                  value={formData.delivery_method}
                  onChange={(e) => setFormData(prev => ({ ...prev, delivery_method: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="EMAIL">📧 Email</option>
                  <option value="EMAIL_HTML">📧✨ HTML Email</option>
                  <option value="SMS">📱 SMS</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Choose how the reminder will be delivered to the pet owner.</p>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  rows={3}
                  placeholder="Message to include in the reminder notification..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Internal Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  placeholder="Internal notes (not sent to pet owner)..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">These notes are for staff only and won't be sent to the pet owner.</p>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.reminder_type_id || !formData.title || !formData.due_date}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Updating...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  Update Reminder
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditReminderModal;

